import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@components/atoms/loaders/loader.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

import { useGetUsageBo } from '@utils/api/back-office/usage/use-get-usage-bo.tsx';
import UsageAnalyticsDashboard from './usage-analytics-dashboard';

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getCurrentMonthDates(): [string, string] {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return [formatDate(start), formatDate(end)];
}

function getLastMonthDates(): [string, string] {
  const now = new Date();
  const firstDayCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayLastMonth = new Date(firstDayCurrentMonth.getTime() - 1);
  const start = new Date(
    lastDayLastMonth.getFullYear(),
    lastDayLastMonth.getMonth(),
    1
  );
  return [formatDate(start), formatDate(lastDayLastMonth)];
}

// Previous 3 full months
function getLastQuarterDates(): [string, string] {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(currentMonth / 3);
  const lastQuarter = currentQuarter - 1;

  if (lastQuarter < 0) {
    const start = new Date(currentYear - 1, 9, 1); // Oct 1
    const end = new Date(currentYear - 1, 11, 31); // Dec 31
    return [formatDate(start), formatDate(end)];
  }

  const startMonth = lastQuarter * 3;
  const start = new Date(currentYear, startMonth, 1);
  const end = new Date(currentYear, startMonth + 3, 0);
  return [formatDate(start), formatDate(end)];
}

function getLastYearDates(): [string, string] {
  const now = new Date();
  const lastYear = now.getFullYear() - 1;
  const start = new Date(lastYear, 0, 1);
  const end = new Date(lastYear, 11, 31);
  return [formatDate(start), formatDate(end)];
}

export default function UsageCharts() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [periodFilter, setPeriodFilter] = useState<string>('current');

  const [period_start, period_end] = useMemo(() => {
    switch (periodFilter) {
      case 'current':
        return getCurrentMonthDates();
      case 'lastMonth':
        return getLastMonthDates();
      case 'quarter':
        return getLastQuarterDates();
      case 'year':
        return getLastYearDates();
      default:
        return getCurrentMonthDates();
    }
  }, [periodFilter]);

  const { data, isLoading, isFetching, isError } = useGetUsageBo({
    period_start,
    period_end
  });

  const result = data?.result || [];

  return (
    <div
      className="bg-app flex-1 p-2"
      style={{ color: getColour('normalText') }}
    >
      {/* Header */}
      <div className="w-full md:px-0 md:py-2 px-4 py-4 border-b border-hairline mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{t('usage-overview')}</h1>
            <small className="text-sm">
              {t('date-range')}:&nbsp;
              <span className="font-mono">
                {period_start} – {period_end}
              </span>
            </small>
          </div>

          <div className="flex items-center gap-x-2">
            {isFetching && (
              <>
                <span
                  className="inline-block h-2 w-2 rounded-full animate-pulse"
                  style={{ backgroundColor: getColour('activeText') }}
                />
                <span className="text-sm">{t('updating-data')}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full">
        {isLoading ? (
          <div className="h-96 w-full flex flex-col justify-center items-center gap-3">
            <Loader />
            <p className="text-xs opacity-80">{t('loading-usage-data')}</p>
          </div>
        ) : isError ? (
          <div className="h-96 w-full flex flex-col justify-center items-center gap-2 text-center">
            {}
            <p className="text-sm font-semibold text-red-500">
              {t('an-error-has-occurred')}
            </p>
            <p className="max-w-md text-xs opacity-80">
              {t('please-adjust-filters-or-try-again')}
            </p>
          </div>
        ) : (
          <UsageAnalyticsDashboard
            data={result}
            periodFilter={periodFilter}
            setPeriodFilter={setPeriodFilter}
          />
        )}
      </div>
    </div>
  );
}
